import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Globe, Check, FloppyDisk, Info } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Account-wide geo-blocking editor for creators.
 *
 * Stored on the user document (`blocked_countries`, `blocked_states`) — applied
 * automatically to every future stream until the creator changes it. No per-stream
 * configuration; this is meant to be a "set once, forget" privacy boundary.
 */
const GeoBlockSettingsCard = () => {
  const [regions, setRegions] = useState({ countries: [], us_states: [], mx_states: [] });
  const [blocked, setBlocked] = useState({ countries: [], states: [] });
  const [original, setOriginal] = useState({ countries: [], states: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const dirty =
    JSON.stringify([...blocked.countries].sort()) !== JSON.stringify([...original.countries].sort()) ||
    JSON.stringify([...blocked.states].sort()) !== JSON.stringify([...original.states].sort());

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [regionsRes, blocksRes] = await Promise.all([
          axios.get(`${API}/api/geo/regions`),
          axios.get(`${API}/api/geo/my-blocks`),
        ]);
        if (cancelled) return;
        setRegions(regionsRes.data);
        const initial = {
          countries: blocksRes.data.blocked_countries || [],
          states: blocksRes.data.blocked_states || [],
        };
        setBlocked(initial);
        setOriginal(initial);
      } catch {
        if (!cancelled) toast.error('Failed to load geo-block settings');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const toggleCountry = (code) => {
    setBlocked((b) => {
      const has = b.countries.includes(code);
      const next = has ? b.countries.filter(c => c !== code) : [...b.countries, code];
      // Also drop any sub-states of an un-blocked country to keep state list clean
      let states = b.states;
      if (has && (code === 'US' || code === 'MX')) {
        const subList = code === 'US' ? regions.us_states : regions.mx_states;
        const codes = subList.map(s => s.code);
        states = b.states.filter(s => !codes.includes(s));
      }
      return { countries: next, states };
    });
  };

  const toggleState = (code) => {
    setBlocked((b) => ({
      ...b,
      states: b.states.includes(code) ? b.states.filter(s => s !== code) : [...b.states, code],
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/geo/my-blocks`, {
        blocked_countries: blocked.countries,
        blocked_states: blocked.states,
      });
      setOriginal(blocked);
      toast.success('Geo-block settings saved — applied to every future stream.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <section id="geo-blocking" className="glass-light rounded-2xl p-6">
        <p className="text-slate-500 text-sm">Loading geo-block settings…</p>
      </section>
    );
  }

  return (
    <section id="geo-blocking" className="glass-light rounded-2xl p-6" data-testid="geo-block-settings-card">
      <div className="flex items-center gap-2 mb-2">
        <Globe weight="fill" className="w-6 h-6 text-blue-400" />
        <h3 className="font-heading text-xl font-bold text-white">Stream Region Block</h3>
        {(blocked.countries.length + blocked.states.length) > 0 && (
          <span className="ml-auto px-2 py-0.5 bg-blue-500/20 text-blue-300 text-xs font-bold rounded-full">
            {blocked.countries.length + blocked.states.length} blocked
          </span>
        )}
      </div>
      <p className="text-slate-400 text-sm mb-5">
        Hide your live stream from viewers in specific countries or sub-regions. Applies to
        every stream you start, until you change it here. Common use: blocking your own
        country or city so people who know you can&apos;t stumble across you.
      </p>

      <div className="flex items-start gap-2 mb-5 p-3 rounded-xl bg-blue-500/5 border border-blue-500/20">
        <Info weight="fill" className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
        <p className="text-xs text-blue-200/80 leading-relaxed">
          Geo-blocking uses the viewer&apos;s IP and isn&apos;t 100% bulletproof — VPNs and proxies
          can defeat it. But for casual passers-by from your own town, it works well.
        </p>
      </div>

      {/* Countries */}
      <div className="mb-4">
        <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Countries</p>
        <div className="flex flex-wrap gap-1.5">
          {regions.countries.map((c) => (
            <button
              key={c.code}
              type="button"
              onClick={() => toggleCountry(c.code)}
              className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                blocked.countries.includes(c.code)
                  ? 'bg-blue-500/30 text-blue-200 ring-1 ring-blue-500/40'
                  : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
              }`}
              data-testid={`settings-geo-country-${c.code}`}
            >
              {blocked.countries.includes(c.code) && <Check className="w-3.5 h-3.5 inline mr-1" />}
              {c.name}
            </button>
          ))}
        </div>
      </div>

      {/* Mexico states */}
      {blocked.countries.includes('MX') && (
        <div className="mb-4 pl-4 border-l-2 border-blue-500/30">
          <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
            Or — block specific Mexican states only (leave MX un-blocked above):
          </p>
          <div className="flex flex-wrap gap-1.5">
            {regions.mx_states.map((s) => (
              <button
                key={s.code}
                type="button"
                onClick={() => toggleState(s.code)}
                className={`px-2 py-1 text-xs rounded-lg transition-colors ${
                  blocked.states.includes(s.code)
                    ? 'bg-blue-500/30 text-blue-200 ring-1 ring-blue-500/40'
                    : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
                }`}
                data-testid={`settings-geo-state-${s.code}`}
              >
                {blocked.states.includes(s.code) && <Check className="w-3 h-3 inline mr-0.5" />}
                {s.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* US states */}
      {blocked.countries.includes('US') && (
        <div className="mb-4 pl-4 border-l-2 border-blue-500/30">
          <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
            Or — block specific US states only:
          </p>
          <div className="flex flex-wrap gap-1.5">
            {regions.us_states.map((s) => (
              <button
                key={s.code}
                type="button"
                onClick={() => toggleState(s.code)}
                className={`px-2 py-1 text-xs rounded-lg transition-colors ${
                  blocked.states.includes(s.code)
                    ? 'bg-blue-500/30 text-blue-200 ring-1 ring-blue-500/40'
                    : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
                }`}
                data-testid={`settings-geo-state-${s.code}`}
              >
                {blocked.states.includes(s.code) && <Check className="w-3 h-3 inline mr-0.5" />}
                {s.name}
              </button>
            ))}
          </div>
        </div>
      )}

      <button
        onClick={save}
        disabled={!dirty || saving}
        className="px-5 py-2.5 bg-blue-500 text-white rounded-xl font-semibold hover:bg-blue-600 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
        data-testid="save-geo-settings-btn"
      >
        <FloppyDisk weight="fill" className="w-4 h-4" />
        {saving ? 'Saving…' : (dirty ? 'Save changes' : 'Saved')}
      </button>
    </section>
  );
};

export default GeoBlockSettingsCard;
